import api from './api';
import { AxiosInstance } from 'axios';
import { ApiResponse, User, BasicResponse, CompanyDetails, CreateCompanyRequest, CreateCompanyResponse, CompanyVerificationStatus, UpdateCompanyUserRequest, CompanyUserResponse, AgentAction, UpdateVerificationStatusRequest } from '../types/api';
import { CompanyRegistrationFormData, CompanyProfileData, CompanySummaryData } from '../types/components';

interface RegistrationStatus {
  isRegistered: boolean;
  isSubmitted?: boolean;
  status: 'approved' | 'pending' | 'not-submitted' | 'not-applicable';
  message: string;
}

const companyService = {
  // Check company registration status
  checkRegistrationStatus: async (companyId: string): Promise<ApiResponse<RegistrationStatus>> => {
    try {
      const response = await (api as AxiosInstance).get(`/company/registration-status/${companyId}`);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Submit company registration
  submitRegistration: async (registrationData: CompanyRegistrationFormData): Promise<ApiResponse<any>> => {
    try {
      const response = await (api as AxiosInstance).post('/company/register', registrationData);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Update company profile
  updateProfile: async (profileData: CompanyProfileData): Promise<ApiResponse<any>> => {
    try {
      const response = await (api as AxiosInstance).put('/company/profile', profileData);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Get company dashboard data
  getDashboardData: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await (api as AxiosInstance).get('/company/dashboard');
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Mock function for development - check if company is registered
  mockCheckRegistrationStatus: (user: User | null): RegistrationStatus => {
    // For development purposes, simulate different registration states
    if (user?.role === 'Company') {
      const isSubmitted = localStorage.getItem('companyRegistrationSubmitted') === 'true';
      const isApproved = localStorage.getItem('companyRegistrationStatus') === 'completed';
      
      return {
        isRegistered: isApproved,
        isSubmitted: isSubmitted,
        status: isApproved ? 'approved' : isSubmitted ? 'pending' : 'not-submitted',
        message: isApproved 
          ? 'Company is fully registered and approved' 
          : isSubmitted 
          ? 'Company registration is pending approval'
          : 'Company registration not submitted'
      };
    }
    return { isRegistered: false, status: 'not-applicable', message: 'Not applicable' };
  },

  // Mock function to simulate registration completion
  mockCompleteRegistration: (): ApiResponse<any> => {
    localStorage.setItem('companyRegistrationStatus', 'completed');
    return { data: { success: true, message: 'Registration completed successfully' }, status: 200, statusText: 'OK', headers: {}, config: {} };
  },

  // Get company details by ID
  getCompanyDetails: async (companyId: string): Promise<BasicResponse<CompanyDetails>> => {
    try {
      const response = await (api as AxiosInstance).get(`/api/Company/companyDetails`, {
        params: { companyId }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  createCompany: async (companyData: CreateCompanyRequest): Promise<BasicResponse<CreateCompanyResponse>> => {
    try {
      const response = await (api as AxiosInstance).post('/api/Company/createCompany', companyData);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  addCompanyUser: async (companyId: string, userId: string, designation: string): Promise<BasicResponse<boolean>> => {
    try {
      const response = await (api as AxiosInstance).post('/api/Company/addCompanyUser', { companyId, userId, designation });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  searchCompanies: async (searchText: string): Promise<BasicResponse<CompanyDetails[]>> => {
    try {
      const response = await (api as AxiosInstance).get('/api/Company/search', { params: { searchText } });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  deleteCompany: async (companyId: string): Promise<BasicResponse<boolean>> => {
    try {
      const response = await (api as AxiosInstance).delete('/api/Company/deleteCompany', { params: { companyId } });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  updateCompanyVerificationStatus: async (companyId: string, regBody: UpdateVerificationStatusRequest): Promise<boolean> => {
    try {
      const response = await (api as AxiosInstance).put(`/api/Company/${companyId}/verification-status`, regBody);
      return response.status == 200;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  updateCompanyUser: async (users: UpdateCompanyUserRequest[]): Promise<BasicResponse<boolean>> => {
    try {
      const response = await (api as AxiosInstance).put('/api/Company/updateCompanyUser', users);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  listCompanyUsers: async (): Promise<BasicResponse<CompanyUserResponse[]>> => {
    try {
      const response = await (api as AxiosInstance).get('/api/Company/listUsers');
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  }
};

export default companyService;
